"use strict";

exports.__esModule = true;
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _clsx = _interopRequireDefault(require("clsx"));
var _reactNumberFormat = _interopRequireDefault(require("react-number-format"));
var _useFormElementValidation = _interopRequireDefault(require("../../hooks/useFormElementValidation"));
var _useRandomId = _interopRequireDefault(require("../../hooks/useRandomId"));
var _localeCurrencies = _interopRequireDefault(require("./locales/localeCurrencies"));
var _useLabel2 = _interopRequireDefault(require("../../hooks/useLabel"));
var _Tooltip = _interopRequireDefault(require("../Tooltip/Tooltip"));
var _Icon = _interopRequireDefault(require("../Icon/Icon"));
var _excluded = ["id", "name", "value", "label", "placeholder", "helper", "floatLabel", "locale", "displayAsText", "displayType", "displayWithTwoDecimals", "clearable", "readOnly", "disabled", "required", "onChange", "onFocus", "onBlur", "onEnterPress", "onError", "className", "addon", "addonPosition", "icon", "iconPosition", "min", "max", "customMessage", "customValidation", "booleanValidation", "withErrorTooltip"];
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
/**
 * The InputCurrency component format currencies according to locale.
 *
 * This component is an opinionated wrapper around <a href="https://github.com/s-yadav/react-number-format">React NumberFormat</a>.
 */

var forceDecimalOuput = function forceDecimalOuput(value, decimalSeparator) {
  var outputValue = value;
  if (value.indexOf(decimalSeparator) > -1 && value.split(decimalSeparator)[1].length < 2) {
    outputValue = value + "0";
  }
  return outputValue;
};
var CustomInput = /*#__PURE__*/_react.default.forwardRef(function (inputProps, ref) {
  return /*#__PURE__*/_react.default.createElement("input", _extends({
    ref: ref,
    className: "uk-inputtable",
    type: "number"
  }, inputProps));
});
var InputCurrency = function InputCurrency(props) {
  var controlledId = props.id,
    name = props.name,
    controlledValue = props.value,
    initialLabel = props.label,
    placeholder = props.placeholder,
    helper = props.helper,
    floatLabel = props.floatLabel,
    locale = props.locale,
    displayAsText = props.displayAsText,
    displayType = props.displayType,
    displayWithTwoDecimals = props.displayWithTwoDecimals,
    clearable = props.clearable,
    readOnly = props.readOnly,
    disabled = props.disabled,
    required = props.required,
    onChange = props.onChange,
    onFocus = props.onFocus,
    onBlur = props.onBlur,
    onEnterPress = props.onEnterPress,
    onError = props.onError,
    className = props.className,
    addon = props.addon,
    addonPosition = props.addonPosition,
    icon = props.icon,
    iconPosition = props.iconPosition,
    min = props.min,
    max = props.max,
    customMessage = props.customMessage,
    customValidation = props.customValidation,
    booleanValidation = props.booleanValidation,
    withErrorTooltip = props.withErrorTooltip,
    attributes = _objectWithoutPropertiesLoose(props, _excluded);
  var inputRef = _react.default.useRef(null);
  var inputCurrencyRef = _react.default.useRef(null);
  var id = _react.default.useRef((0, _useRandomId.default)(controlledId)).current;
  var _getCurrencyAttribute = (0, _localeCurrencies.default)(locale),
    decimalSeparator = _getCurrencyAttribute.decimalSeparator;
  var _React$useState = _react.default.useState(),
    value = _React$useState[0],
    setValue = _React$useState[1];
  var _React$useState2 = _react.default.useState(false),
    error = _React$useState2[0],
    setError = _React$useState2[1];
  var _React$useState3 = _react.default.useState(false),
    focus = _React$useState3[0],
    setFocus = _React$useState3[1];
  var _React$useState4 = _react.default.useState(null),
    currencyAttributesNoValue = _React$useState4[0],
    setCurrencyAttributesNoValue = _React$useState4[1];
  var _useFormElementValida = (0, _useFormElementValidation.default)(value, {
      required: required,
      number: true,
      min: min,
      max: max,
      customMessage: customMessage,
      customValidation: customValidation,
      booleanValidation: booleanValidation,
      currency: required
    }),
    errors = _useFormElementValida[0],
    methods = _useFormElementValida[1];
  var _useLabel = (0, _useLabel2.default)(initialLabel, _extends({}, props, {
      id: id,
      value: value
    })),
    label = _useLabel.label;

  // React.useEffect(() => setValue(controlledValue), [controlledValue]);

  _react.default.useEffect(function () {
    if (controlledValue !== undefined && controlledValue !== null) {
      var tempValue = controlledValue.toString();
      tempValue = forceDecimalOuput(tempValue, decimalSeparator);
      setValue(tempValue);
    } else {
      setValue('0');
    }
  }, [controlledValue, decimalSeparator]);
  _react.default.useEffect(function () {
    setError(errors.length > 0);
    if (typeof onError === 'function') {
      onError({
        name: inputRef.current.name,
        errors: errors.length ? errors : []
      });
    }
  }, [errors, onError]);
  var handleFocus = function handleFocus() {
    setFocus(true);
    document.execCommand('selectall');
    if (typeof onFocus === 'function') {
      onFocus(inputRef.current.value);
    }
  };
  var handleMouseUp = function handleMouseUp() {
    document.execCommand('selectall');
  };
  var handleBlur = function handleBlur() {
    // console.log("handleBlur")
    if (currencyAttributesNoValue) {
      setCurrencyAttributesNoValue(null);
    }
    setFocus(false);
    var valueOnBlur = inputCurrencyRef.current.state.numAsString;
    if (valueOnBlur) {
      valueOnBlur = forceDecimalOuput(valueOnBlur, decimalSeparator);
    } else {
      valueOnBlur = '0';
    }
    setValue(valueOnBlur);
    methods.onChange(valueOnBlur);
    if (typeof onBlur === 'function') {
      onBlur(+valueOnBlur);
    }
  };
  var handleKeyPress = function handleKeyPress(e) {
    if (e.charCode === 13 && typeof onEnterPress === 'function') {
      var valueOnKeyPress = inputCurrencyRef.current.state.numAsString;
      if (valueOnKeyPress) {
        valueOnKeyPress = forceDecimalOuput(valueOnKeyPress, decimalSeparator);
      } else valueOnKeyPress = '0';
      onEnterPress(+valueOnKeyPress, e);
    }
  };
  var handleValueChange = function handleValueChange(valueObject) {
    if (valueObject.floatValue !== +value || !value) {
      var tempValue = '';
      if (!isNaN(valueObject.floatValue)) {
        tempValue = valueObject.floatValue.toString();
        tempValue = forceDecimalOuput(tempValue, decimalSeparator);
        if (currencyAttributesNoValue) {
          setCurrencyAttributesNoValue(null);
        }
      } else if (floatLabel && label) {
        // console.log("No floatValue, empty completely input value content")
        setCurrencyAttributesNoValue({
          prefix: '',
          thousandSeparator: '',
          suffix: ''
        });
      }
      setValue(tempValue);
      if (focus && typeof onChange === 'function') {
        var _valueObject$floatVal;
        onChange((_valueObject$floatVal = valueObject.floatValue) != null ? _valueObject$floatVal : 0);
      }
    }
  };
  var handleOnClear = function handleOnClear() {
    setValue('');
    methods.onChange('');
    if (typeof onChange === 'function') {
      onChange(0, inputCurrencyRef.current.name);
    }
  };
  var inputAddon = /*#__PURE__*/_react.default.isValidElement(addon) ? addon : /*#__PURE__*/_react.default.createElement('span', {}, addon);
  var containerClasses = (0, _clsx.default)('uk-form-item', 'uk-input', 'uk-input--currency', (clearable || (icon == null ? void 0 : icon.length)) && 'has-icon', (icon == null ? void 0 : icon.length) && "has-icon--" + iconPosition, addon && 'has-info', addon && "has-info--" + addonPosition, helper.length > 0 && 'has-helper', errors.length > 0 && 'has-error', errors.length > 0 && withErrorTooltip && 'has-error--tooltip', (errors.length > 0 || disabled || readOnly) && 'is-frozen', clearable && value && 'is-clearable', className);
  var currencyAttributes = locale ? (0, _localeCurrencies.default)(locale) : null;
  if (displayAsText || displayType === 'text') {
    var getValue = function getValue() {
      var formatedValue = value || 0;
      if (displayWithTwoDecimals && !formatedValue.toString().search('.')) {
        formatedValue = value + ".00";
      }
      return formatedValue;
    };
    return /*#__PURE__*/_react.default.createElement(_reactNumberFormat.default, _extends({
      allowEmptyFormatting: true,
      className: className,
      isNumericString: true,
      decimalScale: 2,
      value: getValue(),
      displayType: "text"
    }, currencyAttributes, attributes));
  }
  return /*#__PURE__*/_react.default.createElement("div", {
    className: containerClasses
  }, label, /*#__PURE__*/_react.default.createElement("div", {
    className: "flex-container"
  }, /*#__PURE__*/_react.default.createElement("div", {
    className: "uk-input__field"
  }, /*#__PURE__*/_react.default.createElement(_reactNumberFormat.default, _extends({
    ref: inputCurrencyRef,
    getInputRef: inputRef,
    id: id,
    name: name || id,
    value: value || (focus && !readOnly ? '' : 0),
    placeholder: placeholder && !floatLabel ? placeholder : null,
    allowEmptyFormatting: true,
    autoComplete: "off",
    disabled: disabled,
    readOnly: readOnly,
    required: required,
    isNumericString: true,
    decimalScale: 2,
    onValueChange: handleValueChange,
    onFocus: handleFocus,
    onBlur: handleBlur,
    onMouseUp: handleMouseUp,
    onKeyPress: handleKeyPress,
    customInput: CustomInput,
    "aria-describedby": (name || id) + "-helper-text " + (name || id) + "-required-message",
    "aria-errormessage": (name || id) + "-errors-text",
    "aria-invalid": error
  }, currencyAttributes, attributes, currencyAttributesNoValue)), icon && icon.length && (!clearable || !value) && /*#__PURE__*/_react.default.createElement(_Icon.default, {
    className: (0, _clsx.default)('uk-input__icon', iconPosition),
    name: icon
  }), clearable && value && /*#__PURE__*/_react.default.createElement("div", {
    "aria-hidden": "true",
    className: "uk-input__clearButton " + iconPosition,
    onClick: handleOnClear
  }, "\xD7"), errors.length > 0 && withErrorTooltip && /*#__PURE__*/_react.default.createElement(_Tooltip.default, {
    trigger: "!",
    className: "uk-form-item__error-tooltip"
  }, /*#__PURE__*/_react.default.createElement("div", {
    id: (name || id) + "-errors-text"
  }, errors))), addon && /*#__PURE__*/_react.default.createElement("div", {
    className: (0, _clsx.default)('uk-inputtable__addon', addonPosition)
  }, inputAddon)), errors.length > 0 && !withErrorTooltip && /*#__PURE__*/_react.default.createElement("span", {
    className: "uk-form-item__error",
    id: (name || id) + "-errors-text"
  }, errors), errors.length > 0 && !withErrorTooltip ? '' : helper.length > 0 && /*#__PURE__*/_react.default.createElement("small", {
    className: "uk-form-item__helper",
    id: (name || id) + "-helper-text"
  }, helper));
};
InputCurrency.propTypes = {
  /**
   * ID of the InputCurrency (used to bind the label to its input field)
   * NB: the ID will be randomly generated if not set.
   */
  id: _propTypes.default.string,
  /**
   * Name of the Input (used instead of the ID when submitted)
   */
  name: _propTypes.default.string,
  /**
   * Default value of the Input
   */
  value: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.number]),
  /**
   * Label of the Input
   */
  label: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.element]),
  /**
   * Input placeholder
   */
  placeholder: _propTypes.default.string,
  /**
   * Helper text, is replaced by a message in case of error
   */
  helper: _propTypes.default.string,
  /**
   * Remove the placeholder (if any), replace it with a floating label
   */
  floatLabel: _propTypes.default.bool,
  /**
   * Set the currency formatting depending on the locale
   * (e.g. 'en', 'en-CA', 'en_CA', etc.)
   */
  locale: _propTypes.default.oneOf(['en', 'fr']),
  /**
   * Display the value as a text, wrapped in <span/>
   */
  displayAsText: _propTypes.default.bool,
  /**
   * Display the value always with 2 decimals
   */
  displayWithTwoDecimals: _propTypes.default.bool,
  /**
   * TODO
   * Add a button to clear the Input
   */
  clearable: _propTypes.default.bool,
  /**
   * Set the Input as read-only
   */
  readOnly: _propTypes.default.bool,
  /**
   * Set the Input as disabled
   */
  disabled: _propTypes.default.bool,
  /**
   * Form-validation: set the Input as required
   */
  required: _propTypes.default.bool,
  /**
   * Callback function onChange
   */
  onChange: _propTypes.default.func,
  /**
   * Callback function onFocus
   */
  onFocus: _propTypes.default.func,
  /**
   * Callback function onBlur
   */
  onBlur: _propTypes.default.func,
  /**
   * Callback function onError
   */
  onError: _propTypes.default.func,
  /**
   * Callback function onEnterPress
   */
  onEnterPress: _propTypes.default.func,
  /**
   * Add an informative box next to the input element
   */
  addon: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.element]),
  /**
   * Position the info relative to the input element
   */
  addonPosition: _propTypes.default.oneOf(['left', 'right']),
  /**
   * Add an icon
   */
  icon: _propTypes.default.string,
  /**
   * Position the icon relative to the input element
   */
  iconPosition: _propTypes.default.oneOf(['left', 'right']),
  /**
   * Legacy: will be deprecated in v.1.0.0
   */
  displayType: _propTypes.default.oneOf(['input', 'text']),
  /**
   * Min value required to pass validation
   */
  min: _propTypes.default.number,
  /**
   * Max value required to pass validation
   */
  max: _propTypes.default.number,
  /**
   * Write a custom message when validation triggered an error
   */
  customMessage: _propTypes.default.object,
  /**
   * Add a custom validation with a regex
   */
  customValidation: _propTypes.default.objectOf(_propTypes.default.instanceOf(RegExp)),
  /**
   * Add a custom boolean validation
   */
  booleanValidation: _propTypes.default.bool,
  /**
   * Display error in tooltip otherwise display error below input.
   */
  withErrorTooltip: _propTypes.default.bool
};
InputCurrency.defaultProps = {
  placeholder: '',
  helper: '',
  floatLabel: false,
  displayAsText: false,
  displayWithTwoDecimals: false,
  displayType: 'input',
  clearable: false,
  readOnly: false,
  disabled: false,
  required: false,
  addonPosition: 'right',
  iconPosition: 'right',
  locale: 'fr',
  withErrorTooltip: true
};
var _default = exports.default = InputCurrency;